import type {
  MailDnsRecordCheck,
  MailDnsStatus,
  MailIdentityDnsResult,
  MailIdentityPatchInput,
  PublicMailIdentity,
} from '@embertime/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { api } from '../../lib/api'
import { notify } from '../../lib/notify'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { confirm } from '../ui/confirm-dialog'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'
import {
  addressDomain,
  clockTime,
  DNS_STATUS_TEXT,
  Field,
  IdentityEditSettings,
  identityErrorText,
} from './identity-form-parts'
import { identitiesQueryKey } from './identity-list'

type DnsKey = 'spf' | 'dkim' | 'dmarc'

const RECORDS: ReadonlyArray<{ key: DnsKey; label: string; okText: string }> = [
  { key: 'spf', label: 'SPF', okText: 'Server darf für die Domain senden' },
  { key: 'dkim', label: 'DKIM', okText: 'Signatur-Schlüssel gefunden' },
  { key: 'dmarc', label: 'DMARC', okText: 'Richtlinie gefunden' },
]

const NOT_OK_TEXT: Record<Exclude<MailDnsStatus, 'ok'>, string> = {
  missing: 'Eintrag fehlt noch',
  invalid: 'Eintrag ist ungültig',
  unknown: 'Konnte nicht geprüft werden',
}

const DOT: Record<MailDnsStatus, string> = {
  ok: 'bg-success',
  missing: 'bg-warning',
  invalid: 'bg-warning',
  unknown: 'bg-text-tertiary',
}
const CHIP: Record<MailDnsStatus, string> = {
  ok: 'text-success',
  missing: 'text-warning',
  invalid: 'text-warning',
  unknown: 'text-text-tertiary',
}

/** DNS name relative to the domain: `_dmarc.beispiel.de` → `_dmarc`, the
 * domain itself → `@`. */
export function relativeDnsName(name: string, domain: string): string {
  const n = name.replace(/\.$/, '').toLowerCase()
  const d = domain.toLowerCase()
  if (!d || n === d) return '@'
  return n.endsWith(`.${d}`) ? n.slice(0, -(d.length + 1)) : n
}

/** The stored statuses of a saved identity as a check result without proposals. */
function storedDns(identity: PublicMailIdentity): MailIdentityDnsResult | null {
  if (!identity.dnsCheckedAt || !identity.dnsSpf || !identity.dnsDkim || !identity.dnsDmarc)
    return null
  const rec = (status: MailDnsStatus): MailDnsRecordCheck => ({ status, proposal: null })
  return {
    domain: addressDomain(identity.address),
    spf: rec(identity.dnsSpf),
    dkim: rec(identity.dnsDkim),
    dmarc: rec(identity.dnsDmarc),
    checkedAt: identity.dnsCheckedAt,
  }
}

async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value)
    notify.success('Kopiert')
  } catch {
    notify.error('Kopieren nicht möglich – bitte markieren und kopieren')
  }
}

function DnsRecordRow({
  label,
  okText,
  check,
  domain,
}: {
  label: string
  okText: string
  check: MailDnsRecordCheck | null
  domain: string
}) {
  const status = check?.status
  const proposal = status && status !== 'ok' ? check?.proposal : null
  return (
    <li className="px-3 py-3">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5 shrink-0 rounded-full ring-4 ring-bg-overlay',
            status ? DOT[status] : 'bg-border-default',
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{label}</span>
          <span className="block text-xs text-text-tertiary">
            {!status ? 'Noch nicht geprüft' : status === 'ok' ? okText : NOT_OK_TEXT[status]}
          </span>
        </span>
        {status && (
          <span className={cn('shrink-0 text-sm font-medium', CHIP[status])}>
            {DNS_STATUS_TEXT[status]}
          </span>
        )}
      </div>
      {proposal && (
        <div className="relative mt-2.5 rounded-lg border border-dashed border-border-default bg-bg-base/60 p-3 pr-12">
          <p className="break-all font-mono text-xs text-text-secondary">
            {relativeDnsName(proposal.name, domain)} {proposal.type}
          </p>
          {proposal.value !== null ? (
            <>
              <p className="mt-0.5 break-all font-mono text-xs text-text-primary">
                {proposal.value}
              </p>
              <button
                type="button"
                onClick={() => void copyText(proposal.value ?? '')}
                aria-label={`${label}-Wert kopieren`}
                className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-md text-accent hover:bg-bg-overlay"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          ) : (
            proposal.hint && <p className="mt-0.5 text-xs text-text-secondary">{proposal.hint}</p>
          )}
        </div>
      )}
    </li>
  )
}

/** Form „Eigene Domain“ (#839, Mockup „Eigene Domain“): create, or edit a saved
 * identity (address fixed). „Prüfen“ checks an unsaved address through the
 * dns-check route of the account, a saved one through its own route. */
export function IdentityOwnDomainSheet({
  open,
  accountId,
  accountLabel,
  identity,
  onBack,
  onClose,
  onSaved,
}: {
  open: boolean
  accountId: string
  accountLabel: string
  /** Saved identity to edit; absent = create. */
  identity?: PublicMailIdentity
  onBack: () => void
  onClose: () => void
  onSaved: () => void
}) {
  const qc = useQueryClient()
  const [address, setAddress] = useState(identity?.address ?? '')
  const [displayName, setDisplayName] = useState(identity?.displayName ?? '')
  const [isDefault, setIsDefault] = useState(identity?.isDefault ?? false)
  const [active, setActive] = useState((identity?.status ?? 'active') === 'active')
  const [dns, setDns] = useState<MailIdentityDnsResult | null>(() =>
    identity ? storedDns(identity) : null,
  )
  const [error, setError] = useState('')
  const domain = dns?.domain ?? addressDomain(address)
  const invalidate = () => qc.invalidateQueries({ queryKey: identitiesQueryKey(accountId) })

  const check = useMutation({
    mutationFn: async (): Promise<MailIdentityDnsResult> => {
      if (identity) {
        const res = await api.checkMailIdentityDns(accountId, identity.id)
        void invalidate()
        return res.dns
      }
      return (await api.checkMailIdentityDnsUnsaved(accountId, address.trim().toLowerCase())).dns
    },
    onSuccess: (result) => {
      setError('')
      setDns(result)
    },
    onError: (err) => setError(identityErrorText(err)),
  })

  const save = useMutation({
    mutationFn: async () => {
      const name = displayName.trim() || null
      if (!identity) {
        const { identity: created } = await api.createMailIdentity(accountId, {
          kind: 'own_domain',
          address: address.trim().toLowerCase(),
          displayName: name,
        })
        // Checked before saving: store the result on the new identity too, so
        // the list shows its DNS state (best effort, the identity is saved).
        if (dns) {
          try {
            await api.checkMailIdentityDns(accountId, created.id)
          } catch {
            // the list shows „DNS ?“ until the next check
          }
        }
        return
      }
      const patch: MailIdentityPatchInput = {}
      if (name !== identity.displayName) patch.displayName = name
      const status = active ? 'active' : 'disabled'
      if (status !== identity.status) patch.status = status
      if (active && isDefault !== identity.isDefault) patch.isDefault = isDefault
      if (Object.keys(patch).length > 0) await api.updateMailIdentity(accountId, identity.id, patch)
    },
    onSuccess: () => {
      void invalidate()
      onSaved()
    },
    onError: (err) => setError(identityErrorText(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => api.deleteMailIdentity(accountId, id),
    onSuccess: () => {
      void invalidate()
      onSaved()
    },
    onError: (err) => setError(identityErrorText(err)),
  })

  async function askDelete() {
    if (!identity) return
    if (
      await confirm({
        title: 'Absender löschen?',
        description: `${identity.address} steht danach nicht mehr bei „Von“ zur Wahl. Gesendete Mails bleiben unverändert.`,
        confirmLabel: 'Löschen',
        variant: 'danger',
      })
    )
      remove.mutate(identity.id)
  }

  const busy = save.isPending || remove.isPending
  const allOk =
    !!dns && dns.spf.status === 'ok' && dns.dkim.status === 'ok' && dns.dmarc.status === 'ok'
  const canCheck = !!identity || addressDomain(address).includes('.')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Eigene Domain"
      description={`Absender für „${accountLabel}“`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={identity ? onClose : onBack}>
            Zurück
          </Button>
          <Button
            type="submit"
            form="identity-own-domain"
            disabled={busy || (!identity && !address.trim())}
          >
            Speichern
          </Button>
        </>
      }
    >
      <form
        id="identity-own-domain"
        className="space-y-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (!busy) save.mutate()
        }}
      >
        {error && (
          <p role="alert" className="rounded-lg bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        )}
        <Field id="identity-address" label="Adresse">
          <Input
            id="identity-address"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={address}
            disabled={!!identity}
            placeholder="info@agentur-beispiel.de"
            onChange={(e) => {
              setAddress(e.target.value)
              if (dns && addressDomain(e.target.value) !== dns.domain) setDns(null)
            }}
            className="h-12"
          />
        </Field>
        <Field id="identity-name" label="Anzeigename">
          <Input
            id="identity-name"
            value={displayName}
            maxLength={200}
            placeholder="Agentur Beispiel"
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-12"
          />
        </Field>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm text-text-secondary">DNS für {domain || '…'}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canCheck || check.isPending}
              onClick={() => check.mutate()}
            >
              <RefreshCw
                className={cn('h-4 w-4', check.isPending && 'animate-spin')}
                aria-hidden="true"
              />
              Prüfen
            </Button>
          </div>
          <ul className="divide-y divide-border-subtle rounded-2xl border border-border-subtle">
            {RECORDS.map((record) => (
              <DnsRecordRow
                key={record.key}
                label={record.label}
                okText={record.okText}
                check={dns ? dns[record.key] : null}
                domain={domain}
              />
            ))}
          </ul>
          {dns && (
            <p className="text-xs text-text-tertiary">
              Zuletzt geprüft {clockTime(dns.checkedAt)} · DNS-Änderungen brauchen bis zu einer
              Stunde.
              {!allOk && ' Senden geht schon, landet ohne DMARC aber eher im Spam.'}
            </p>
          )}
        </div>
        {identity && (
          <IdentityEditSettings
            isDefault={isDefault}
            active={active}
            disabled={busy}
            onDefaultChange={setIsDefault}
            onActiveChange={(value) => {
              setActive(value)
              if (!value) setIsDefault(false)
            }}
            onDelete={() => void askDelete()}
          />
        )}
      </form>
    </Dialog>
  )
}
